import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AiDraft from "@/components/partner/AiDraft";
import { errMsg } from "@/lib/api";

const SELECTS = {
  duration_type: [["weekend", "Weekend"], ["full-day", "Full day"], ["multi-day", "Multi-day"]],
  region: [["pakistan", "Pakistan"], ["outbound", "Outbound"]],
  audience: [["public", "Public cohort"], ["women-only", "Women-only"]],
  setting: [["mountains", "Mountains"], ["forest", "Forest"], ["lake", "Lake"], ["river", "River"], ["coast", "Coast"], ["desert", "Desert"], ["city", "City"]],
};

const F = ({ label, children, className = "" }) => <div className={className}><Label className="text-xs capitalize text-muted-foreground">{label}</Label><div className="mt-1.5">{children}</div></div>;

function Sel({ k, opts, f, set }) {
  return (
    <Select value={f[k] || ""} onValueChange={(v) => set(k, v)}>
      <SelectTrigger data-testid={`basics-${k}`}><SelectValue placeholder="Choose" /></SelectTrigger>
      <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function ListEditor({ label, items, onChange, tid }) {
  return (
    <F label={label}>
      <div className="space-y-2">
        {items.map((x, i) => (
          <div key={i} className="flex gap-2">
            <Input value={x} onChange={(e) => onChange(items.map((y, j) => (j === i ? e.target.value : y)))} data-testid={`${tid}-${i}`} />
            <Button size="icon" variant="ghost" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => onChange([...items, ""])} data-testid={`${tid}-add`}><Plus className="mr-1 h-3 w-3" />Add</Button>
      </div>
    </F>
  );
}

export default function BasicsTab({ data, api, reload }) {
  const l = data.listing;
  const [f, setF] = useState(l);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const hs = f.host_stack || { organizer: {}, operator: {}, curators: [] };
  const setHs = (patch) => set("host_stack", { ...hs, ...patch });
  const locked = data.active_bookings > 0;

  const save = async () => {
    const keys = ["title", "tagline", "description", "vibe", "destination", "region", "setting", "duration_type", "days", "start_date", "end_date", "price_pkr", "capacity", "audience", "private_buy_enabled", "private_price_pkr", "hero_image", "inclusions", "exclusions", "host_stack", "support_phone", "meeting_point"];
    const body = Object.fromEntries(keys.map((k) => [k, f[k]]).filter(([, v]) => v !== undefined && v !== null && v !== ""));
    ["days", "price_pkr", "capacity", "private_price_pkr"].forEach((k) => { if (body[k] !== undefined) body[k] = Number(body[k]); });
    body.inclusions = (f.inclusions || []).filter((x) => x.trim());
    body.exclusions = (f.exclusions || []).filter((x) => x.trim());
    try { await api.put(`/partner/listings/${l.id}`, body); toast.success("Saved"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  const applyStory = (t) => {
    const tag = t.match(/Tagline:\s*(.+)/i)?.[1]?.trim();
    const story = t.split(/Story:/i)[1]?.trim();
    if (tag) set("tagline", tag);
    set("description", story || t);
    toast.info("Draft applied to the form. Review, then save.");
  };

  return (
    <div className="space-y-8" data-testid="basics-tab">
      {locked && <p className="rounded-lg bg-muted p-3 text-sm text-muted-foreground" data-testid="basics-locked-note">Price, capacity, dates and private-buy are locked while {data.active_bookings} confirmed booking(s) exist.</p>}
      <div className="grid gap-5 md:grid-cols-2">
        <F label="Title" className="md:col-span-2"><Input value={f.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="basics-title" /></F>
        <F label="Tagline" className="md:col-span-2"><Input value={f.tagline || ""} onChange={(e) => set("tagline", e.target.value)} data-testid="basics-tagline" /></F>
        <F label="Story" className="md:col-span-2"><Textarea rows={5} value={f.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="basics-description" /></F>
        <div className="md:col-span-2"><AiDraft kind="listing" context={() => `Title: ${f.title}\nDestination: ${f.destination}\nVibe: ${f.vibe}\nDays: ${f.days}\nInclusions: ${(f.inclusions || []).join(", ")}`} onApply={applyStory} /></div>
        <F label="Vibe"><Sel k="vibe" opts={data.vibes.map((v) => [v.key, v.name])} f={f} set={set} /></F>
        <F label="Destination"><Input value={f.destination || ""} onChange={(e) => set("destination", e.target.value)} data-testid="basics-destination" /></F>
        {Object.entries(SELECTS).map(([k, opts]) => <F key={k} label={k.replace("_", " ")}><Sel k={k} opts={opts} f={f} set={set} /></F>)}
        <F label="Days"><Input type="number" min={1} disabled={locked} value={f.days || ""} onChange={(e) => set("days", e.target.value)} data-testid="basics-days" /></F>
        <F label="Start date"><Input type="date" disabled={locked} value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="basics-start-date" /></F>
        <F label="End date"><Input type="date" disabled={locked} value={f.end_date || ""} onChange={(e) => set("end_date", e.target.value)} data-testid="basics-end-date" /></F>
        <F label="Price per person (PKR)"><Input type="number" disabled={locked} value={f.price_pkr || ""} onChange={(e) => set("price_pkr", e.target.value)} data-testid="basics-price" /></F>
        <F label="Capacity"><Input type="number" disabled={locked} value={f.capacity || ""} onChange={(e) => set("capacity", e.target.value)} data-testid="basics-capacity" /></F>
        <div className="flex items-center gap-3 md:col-span-2">
          <Switch checked={!!f.private_buy_enabled} disabled={locked} onCheckedChange={(v) => set("private_buy_enabled", v)} data-testid="basics-private-switch" />
          <span className="text-sm">Allow private group buy-out (closes automatically once any seat sells)</span>
        </div>
        {f.private_buy_enabled && <F label="Private buy-out price (PKR, whole group)"><Input type="number" disabled={locked} value={f.private_price_pkr || ""} onChange={(e) => set("private_price_pkr", e.target.value)} data-testid="basics-private-price" /></F>}
        <div className="md:col-span-2"><AiDraft kind="pricing" context={() => `Destination: ${f.destination}, ${f.days} days, capacity ${f.capacity}, inclusions: ${(f.inclusions || []).join(", ")}. Current price PKR ${f.price_pkr}.`} /></div>
        <F label="Hero image URL" className="md:col-span-2"><Input value={f.hero_image || ""} onChange={(e) => set("hero_image", e.target.value)} placeholder="https://…" data-testid="basics-hero-image" /></F>
        <F label="On-trip support phone"><Input value={f.support_phone || ""} onChange={(e) => set("support_phone", e.target.value)} data-testid="basics-support-phone" /></F>
        <F label="Meeting point"><Input value={f.meeting_point || ""} onChange={(e) => set("meeting_point", e.target.value)} data-testid="basics-meeting-point" /></F>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <ListEditor label="Inclusions" items={f.inclusions || []} onChange={(v) => set("inclusions", v)} tid="inclusion" />
        <ListEditor label="Exclusions" items={f.exclusions || []} onChange={(v) => set("exclusions", v)} tid="exclusion" />
      </div>
      <div>
        <h3 className="text-xl text-primary">Host stack</h3>
        <p className="text-sm text-muted-foreground">One organizer and one operator per listing, plus any number of curators.</p>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <F label="Organizer"><Input value={hs.organizer?.name || ""} onChange={(e) => setHs({ organizer: { ...hs.organizer, name: e.target.value } })} data-testid="host-organizer" /></F>
          <F label="Operator name"><Input value={hs.operator?.name || ""} onChange={(e) => setHs({ operator: { ...hs.operator, name: e.target.value } })} data-testid="host-operator" /></F>
          <F label="Property / location"><Input value={hs.operator?.property || ""} onChange={(e) => setHs({ operator: { ...hs.operator, property: e.target.value } })} data-testid="host-property" /></F>
        </div>
        <div className="mt-4 space-y-2">
          {(hs.curators || []).map((c, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="Curator name" value={c.name} onChange={(e) => setHs({ curators: hs.curators.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} data-testid={`host-curator-name-${i}`} />
              <Input placeholder="Role" value={c.role} onChange={(e) => setHs({ curators: hs.curators.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)) })} data-testid={`host-curator-role-${i}`} />
              <Button size="icon" variant="ghost" onClick={() => setHs({ curators: hs.curators.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setHs({ curators: [...(hs.curators || []), { name: "", role: "" }] })} data-testid="host-curator-add"><Plus className="mr-1 h-3 w-3" />Add curator</Button>
        </div>
      </div>
      <Button className="rounded-full px-8" onClick={save} data-testid="basics-save-btn">Save basics</Button>
    </div>
  );
}
